import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap-v5';
import {
  KTSVG,
  toAbsoluteUrl,
  SimpleAlert,
  maskPhoneNumber,
  formatPrice,
} from '../../../../../_mexablock/helpers';
import { NumericFormat } from 'react-number-format';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { StepperComponent } from '../../../../../_mexablock/assets/ts/components';
import {
  validateICCID,
  validateEmail,
} from '../../../../service/operacion/Validacion';
import * as auth from '../../../../modules/auth/redux/AuthRedux';
import { useSelectOffers } from '../hooks/ActivacionHook';
import { createActivation } from '../../../../service/operacion/Activaciones';
import { getUserByToken } from '../../../../modules/auth/redux/AuthCRUD';

type Props = {
  show: boolean;
  productType?: 'mifi' | 'hogar';
  handleClose: (updateData: boolean) => void;
};

const schemaNewActivationMiFi = [
  Yup.object({
    first_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    mother_last_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    father_last_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    email: Yup.string()
      .email('Formato de correo inválido.')
      .min(3, 'Mínimo 3 caracteres.')
      .max(50, 'Máximo 50 caracteres.')
      .required('Requerido.'),
  }),
  Yup.object({
    iccid: Yup.string()
      .required('Requerido.')
      .min(19, 'Formato de ICCID inválido')
      .max(19, 'Formato de ICCID inválido'),
  }),
  Yup.object({
    offer_id: Yup.number().min(1, 'Requerido.').required('Requerido.'),
  }),
];

let maxDate = new Date();
maxDate.setDate(maxDate.getDate() + 1);

const initValues = {
  iccid: '',
  schedule_date: maxDate,
  offer_id: 0,
  inmediality: 1,
  first_name: '',
  mother_last_name: '',
  father_last_name: '',
  email: '',
};

const NewActivationMiFiModal: React.FC<Props> = ({ show, productType = 'mifi', handleClose }) => {
  const dispatch = useDispatch();
  const { data: offers } = useSelectOffers(show, productType === 'hogar' ? 'activation_ho' : 'activation_mifi');
  const [loading, setLoading] = useState(false);
  const [PhoneNumber, setPhoneNumber] = useState<string>('');
  const [textStepper, setTextStepper] = useState('Continuar');
  const [currentSchema, setCurrentSchema] = useState(schemaNewActivationMiFi[0]);
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(
      stepperRef.current as HTMLDivElement
    );
  };

  useEffect(() => {
    if (show) {
      loadStepper();
    }
  }, [stepperRef, show]);

  const formNewActivationMiFi = useFormik({
    initialValues: initValues,
    validationSchema: currentSchema,
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, setFieldError }) => {
      if (!stepper.current) {
        return;
      }
      switch (stepper.current.currentStepIndex) {
        case 1:
          setLoading(true);
          setTimeout(() => {
            validateEmail(values)
              .then(async (res: any) => {
                if (!stepper.current) {
                  return;
                }
                const { disponible, mensaje } = res.data.doc.data;
                if (disponible) {
                  stepper.current.goNext();
                  setCurrentSchema(schemaNewActivationMiFi[1]);
                  setLoading(false);
                } else {
                  SimpleAlert(mensaje, 3000, 'error');
                  setLoading(false);
                }
              })
              .catch((err) => {
                let resMessageToast: string = '';
                const {
                  data: { message },
                } = err.response || { data: { message: 'Error de validación' } };
                resMessageToast = `${message}`;
                SimpleAlert(resMessageToast, 3000, 'error');
                setLoading(false);
              });
          }, 1000);
          break;
        case 2:
          setLoading(true);
          setTimeout(() => {
            validateICCID({ ...values, type: productType === 'hogar' ? 'activation_hogar' : 'activation_mifi' })
              .then(async (res) => {
                const { success, error, activation } = res.data.doc.data;
                if (!activation) {
                  if (!stepper.current) {
                    return;
                  }
                  if (success) {
                    stepper.current.goNext();
                    setCurrentSchema(schemaNewActivationMiFi[2]);
                    setLoading(false);
                    setTextStepper('Activar');
                  } else {
                    SimpleAlert(error, 3000, 'error');
                    setLoading(false);
                  }
                } else {
                  SimpleAlert('ICCID YA ACTIVO.', 3000, 'error');
                  setLoading(false);
                }
              })
              .catch((err) => {
                SimpleAlert(
                  'Error al validar el ICCID, revisa que el ICCID esté correcto.',
                  3000,
                  'error'
                );
                setLoading(false);
              });
          }, 1000);
          break;
        case 3:
          setLoading(true);
          setTimeout(() => {
            const getCoordinates = () => new Promise((resolve) => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => resolve(`${position.coords.latitude}, ${position.coords.longitude}`),
                  (error) => resolve("25.68119839637786, -100.34750172456444") // fallback
                );
              } else {
                resolve("25.68119839637786, -100.34750172456444"); // fallback
              }
            });

            getCoordinates().then((address) => {
              const body = {
                ...values,
                product_type: productType,
                address
              };
              createActivation(body)
              .then(async (res) => {
                if (!stepper.current) {
                  return;
                }
                const msisdn = res.data?.doc?.data?.msisdn || '';
                stepper.current.goNext();
                setTextStepper('Finalizar');
                setPhoneNumber(msisdn);
                SimpleAlert(
                  `La activación ${productType === 'hogar' ? 'Internet Hogar' : 'MiFi'} se procesó exitosamente`,
                  3000,
                  'success'
                );
                setLoading(false);

                // UPDATE SALDO
                const user = await getUserByToken();
                dispatch(auth.actions.fulfillUser(user));
              })
              .catch((err) => {
                let resMessageToast: string = '';
                if (err.response?.data?.message) {
                  resMessageToast = err.response.data.message;
                } else if (err.request) {
                  resMessageToast =
                    'Error de red: Verifique su conexión a Internet';
                } else {
                  resMessageToast = err.message || 'Error desconocido';
                }
                SimpleAlert(resMessageToast, 3000, 'error');
                setLoading(false);
              });
            });
          }, 1000);
          break;
        case 4:
          if (!stepper.current) {
            return;
          }
          handleClose(true);
          stepper.current.goto(1);
          setTextStepper('Continuar');
          setPhoneNumber('');
          setCurrentSchema(schemaNewActivationMiFi[0]);
          formNewActivationMiFi.resetForm();
          setLoading(false);
          break;
      }
    },
  });

  const prevStep = () => {
    if (!stepper.current) {
      return;
    }
    stepper.current?.goPrev();
    const prevIndex = stepper.current.currentStepIndex - 1;
    if (prevIndex >= 0 && schemaNewActivationMiFi[prevIndex]) {
      setCurrentSchema(schemaNewActivationMiFi[prevIndex]);
    }
    switch (stepper.current.currentStepIndex) {
      case 1:
        setTextStepper('Continuar');
        break;
      case 2:
        setTextStepper('Continuar');
        break;
      case 3:
        setTextStepper('Activar');
        break;
    }
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const value = e.target.value;
    const valueReg = value.replace(/\d/g, '');
    const cleanInput = valueReg.replace(/^\s+|\s+(?=\s)/g, '');
    const inputLimpio = cleanInput.replace(/[^\wáéíóúüÁÉÍÓÚÜñÑ\s]/g, '');
    formNewActivationMiFi.setFieldValue(field, inputLimpio);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const radioValue = Number(e.target.value);
    formNewActivationMiFi.setFieldValue('inmediality', radioValue);
  };

  return (
    <Modal
      id="kt_modal_new_activation_mifi"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-900px"
      show={show}
      onHide={() => handleClose(false)}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={() => handleClose(false)}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Nueva Activación - {productType === 'hogar' ? 'Internet Hogar' : 'MiFi'}</h1>
          </div>
          <div
            ref={stepperRef}
            className="stepper stepper-links d-flex flex-column gap-5 first"
            id="kt_modal_create_app_stepper"
          >
            <div className="stepper-nav justify-content-center py-2">
              <div
                className="stepper-item current"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Datos del Cliente</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Datos de la Activación</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Ofertas</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Completada</h3>
              </div>
            </div>
            <form
              className="mx-auto w-100 mw-800px pt-4 pb-4"
              id="kt_modal_top_up_wallet_stepper_form"
              autoComplete="off"
              onSubmit={formNewActivationMiFi.handleSubmit}
            >
              <div
                className="current"
                data-kt-stepper-element="content"
              >
                <div className="w-100 ms-20 me-20 h-auto">

                  <div className="fv-row mb-7 fv-plugins-icon-container">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Nombre
                          </label>
                          {formNewActivationMiFi.touched.first_name &&
                            formNewActivationMiFi.errors.first_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationMiFi.errors.first_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Nombre"
                          {...formNewActivationMiFi.getFieldProps('first_name')}
                          className="form-control mb-3"
                          type="text"
                          name="first_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'first_name');
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Apellido paterno
                          </label>
                          {formNewActivationMiFi.touched.father_last_name &&
                            formNewActivationMiFi.errors.father_last_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationMiFi.errors.father_last_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Apellido paterno"
                          {...formNewActivationMiFi.getFieldProps(
                            'father_last_name'
                          )}
                          className="form-control mb-3"
                          type="text"
                          name="father_last_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'father_last_name');
                          }}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Apellido materno
                          </label>
                          {formNewActivationMiFi.touched.mother_last_name &&
                            formNewActivationMiFi.errors.mother_last_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationMiFi.errors.mother_last_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Apellido materno"
                          {...formNewActivationMiFi.getFieldProps(
                            'mother_last_name'
                          )}
                          className="form-control mb-3"
                          type="text"
                          name="mother_last_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'mother_last_name');
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Correo
                          </label>
                          {formNewActivationMiFi.touched.email &&
                            formNewActivationMiFi.errors.email && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationMiFi.errors.email}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Correo electrónico"
                          {...formNewActivationMiFi.getFieldProps('email')}
                          className="form-control mb-3 mb-lg-0"
                          type="text"
                          name="email"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100 ms-20 me-20 h-auto">
                  <div className="fv-row mb-7 fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fs-6 fw-semibold form-label mb-2">
                        ICCID
                      </label>
                      {formNewActivationMiFi.touched.iccid &&
                        formNewActivationMiFi.errors.iccid && (
                          <div className="fv-plugins-message-container text-danger w-auto ms-5">
                            <span role="alert">
                              {formNewActivationMiFi.errors.iccid}
                            </span>
                          </div>
                        )}
                    </div>

                    <NumericFormat
                      className="form-control mb-3"
                      {...formNewActivationMiFi.getFieldProps('iccid')}
                      decimalScale={0}
                      maxLength={19}
                      fixedDecimalScale
                      placeholder="Identificador (ICCID)"
                      prefix=""
                      onValueChange={(values: any) => {
                        const { floatValue } = values;
                        formNewActivationMiFi.setFieldValue('iccid', floatValue);
                      }}
                    />
                  </div>

                </div>
              </div>

              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100 ms-20 me-20 h-auto">
                  <div className="fv-row mb-7 fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fs-6 fw-semibold form-label mb-2">
                        Selecciona un paquete MiFi
                      </label>
                      {formNewActivationMiFi.touched.offer_id &&
                        formNewActivationMiFi.errors.offer_id && (
                          <div className="fv-plugins-message-container text-danger w-auto ms-5">
                            <span role="alert">
                              {formNewActivationMiFi.errors.offer_id}
                            </span>
                          </div>
                        )}
                    </div>
                    {offers.length === 0 && (
                      <div className="alert alert-warning p-4">
                        No hay ofertas MiFi disponibles en este momento.
                      </div>
                    )}
                    {offers.map((item: any, index: number) => (
                      <a
                        href="#/"
                        key={index}
                        className={clsx(
                          'btn btn-outline btn-flex flex-column flex-stack pb-5 ms-5 mt-5 border-hover-primary float-start',
                          {
                            'btn-active-light-primary active':
                              formNewActivationMiFi.values.offer_id === item.id,
                          }
                        )}
                        style={{
                          width: '200px',
                          height: '150px',
                          border: 'solid 1px #dbdfe9',
                        }}
                        onClick={() => {
                          formNewActivationMiFi.setFieldValue(`offer_id`, item.id);
                        }}
                      >
                        <div className="">
                          <span
                            className={clsx(
                              ' fw-bold fs-2 d-block text-hover-primary ',
                              {
                                ' text-primary':
                                  formNewActivationMiFi.values.offer_id === item.id,
                                ' text-gray-800':
                                  formNewActivationMiFi.values.offer_id !== item.id,
                              }
                            )}
                          >
                            {`${item.gb} GB`}
                          </span>
                          <span
                            className={clsx(' fw-semibold fs-5', {
                              ' text-primary':
                                formNewActivationMiFi.values.offer_id === item.id,
                              ' text-gray-600':
                                formNewActivationMiFi.values.offer_id !== item.id,
                            })}
                          >
                            {`${formatPrice(item.price, 'es-MX', 'MXN', 2, 2)}`}
                          </span>
                          <span
                            className={clsx('  fw-semibold fs-7', {
                              ' text-primary':
                                formNewActivationMiFi.values.offer_id === item.id,
                              ' text-gray-500':
                                formNewActivationMiFi.values.offer_id !== item.id,
                            })}
                          >
                            {` / ${item.commercial_time || item.days + ' días'}`}
                          </span>
                        </div>

                        <div
                          className={clsx(' fw-semibold fs-6 mt-1', {
                            ' text-primary':
                              formNewActivationMiFi.values.offer_id === item.id,
                            ' text-gray-600':
                              formNewActivationMiFi.values.offer_id !== item.id,
                          })}
                        >
                          {item.share_internet ? (
                            <KTSVG
                              className="svg-icon-3 svg-icon-success"
                              path="/media/icons/duotune/arrows/arr016.svg"
                            />
                          ) : (
                            <KTSVG
                              className="svg-icon-3"
                              path="/media/icons/duotune/arrows/arr015.svg"
                            />
                          )}
                          Comparte internet
                        </div>

                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100">
                  <div className="text-center">
                    <h1 className="fw-bold text-primary">
                      Activación MiFi exitosa!
                    </h1>

                    <div className="fw-semibold text-muted fs-4">
                      Este es tu número de línea MiFi:
                    </div>
                    <h1 className="fw-bold text-dark mt-10">
                      {PhoneNumber ? maskPhoneNumber(PhoneNumber) : 'Número no disponible'}
                    </h1>
                  </div>
                  <div className="text-center px-4">
                    <img
                      className="mx-auto h-300px h-lg-300px"
                      src={toAbsoluteUrl(
                        '/media/illustrations/sketchy-1/ok.png'
                      )}
                      alt=""
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex flex-stack pt-10">
                <div className="me-2">
                  {PhoneNumber === '' && (
                    <button
                      onClick={prevStep}
                      type="button"
                      className="btn btn-lg btn-light-primary me-3"
                      data-kt-stepper-action="previous"
                    >
                      <KTSVG
                        path="/media/icons/duotune/arrows/arr063.svg"
                        className="svg-icon-4 me-1"
                      />
                      Regresar
                    </button>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    className="btn btn-lg btn-primary me-3"
                    disabled={loading}
                  >
                    {!loading && (
                      <>
                        <span className="indicator-label">{textStepper}</span>
                        {textStepper === 'Continuar' && (
                          <KTSVG
                            path="/media/icons/duotune/arrows/arr064.svg"
                            className="svg-icon-4 me-1 ms-1"
                          />
                        )}
                      </>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: 'block' }}
                      >
                        Espere por favor...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { NewActivationMiFiModal };
